import { ApiSalesDashboardService } from 'services';
import {
	KpiEnvelope,
	IProductionItem,
	IProductionByTeamItem,
	IMonthlySeriesItem,
	IMonthlySeriesByTeamItem,
	ISummaryCardItem,
	IProductionDecompositionItem,
	IProductionMixItem,
	INewOrdersItem,
	IPendingBalanceItem,
	IPendingBalanceSummaryItem,
	IUpcomingTravelItem,
	IPendingIssueItem,
	ICollectionsItem,
	IGoalAttainmentItem,
	IGoalAttainmentTeamTotal,
	ILeaderboardItem,
	INegativeVariationItem,
	ICriticalAlertItem,
	IConversionItem,
	IConversionByTeamItem,
	IPendingConversionData,
	IPipelineData,
	IDashboardWorkflowItem,
	IOperationalDashboardData,
	IOperationalRequestItem,
	IOperationalRequestSummaryItem,
	IOperationalRequestByWorkflowItem,
	IOperationalRequestOverview,
	IStepClassificationItem,
	IVariationItem,
	StepClassification,
	StepClassificationWritable,
	IWorkflowExclusionItem,
	ISyncStatusItem,
	ISyncAcceptedResponse,
} from './types';

// ─── Helpers internos ────────────────────────────────────────────────────────

// Paths sin / inicial: baseURL ya incluye /api, así axios resuelve
// http://localhost:3000/api + data/sales/... = http://localhost:3000/api/data/sales/...
// Si el path empezara con / el baseURL path sería ignorado.

type Params = Record<string, string | number | boolean | undefined>;

async function kpiGet<T>(path: string, params?: Params): Promise<{ data: T; cachedAt: string | null }> {
	const cleanParams = params
		? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
		: undefined;
	const response = await ApiSalesDashboardService.get<KpiEnvelope<T>>(path, { params: cleanParams });
	if (!response.data.success) {
		throw new Error(`API error: success=false on ${path}`);
	}
	return {
		data: response.data.data,
		cachedAt: response.data.metadata?.cachedAt ?? null,
	};
}

async function adminGet<T>(path: string, params?: Params): Promise<T> {
	const cleanParams = params
		? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
		: undefined;
	const response = await ApiSalesDashboardService.get<T>(path, { params: cleanParams });
	return response.data;
}

async function adminPost<T>(path: string, body?: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.post<T>(path, body);
	return response.data;
}

async function adminPatch<T>(path: string, body: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.patch<T>(path, body);
	return response.data;
}

async function adminDelete<T>(path: string): Promise<T> {
	const response = await ApiSalesDashboardService.delete<T>(path);
	return response.data;
}

async function adminPut<T>(path: string, body: unknown): Promise<T> {
	const response = await ApiSalesDashboardService.put<T>(path, body);
	return response.data;
}

// Alias para acortar los return types de las funciones KPI
type KpiResult<T> = Promise<{ data: T; cachedAt: string | null }>;

// ─── 2. Sales KPI ────────────────────────────────────────────────────────────

// 2.1 Producción

export const getProduction = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string; refresh?: boolean; maxAge?: string },
): KpiResult<IProductionItem[]> => kpiGet<IProductionItem[]>('data/sales/production', { from, to, ...options });

export const getProductionByTeam = (
	from: string,
	to: string,
	options?: { team?: string; refresh?: boolean; maxAge?: string },
): KpiResult<IProductionByTeamItem[]> =>
	kpiGet<IProductionByTeamItem[]>('data/sales/production/by-team', { from, to, ...options });

export const getMonthlySeries = (options?: {
	sellerId?: number;
	team?: string;
	refresh?: boolean;
	maxAge?: string;
}): KpiResult<IMonthlySeriesItem[]> => kpiGet<IMonthlySeriesItem[]>('data/sales/production/monthly-series', options);

export const getMonthlySeriesByTeam = (options?: {
	team?: string;
	refresh?: boolean;
	maxAge?: string;
}): KpiResult<IMonthlySeriesByTeamItem[]> =>
	kpiGet<IMonthlySeriesByTeamItem[]>('data/sales/production/monthly-series/by-team', options);

export const getSummaryCard = (options?: {
	sellerId?: number;
	team?: string;
	refresh?: boolean;
	maxAge?: string;
}): KpiResult<ISummaryCardItem[]> => kpiGet<ISummaryCardItem[]>('data/sales/production/summary-card', options);

export const getProductionDecomposition = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IProductionDecompositionItem[]> =>
	kpiGet<IProductionDecompositionItem[]>('data/sales/production/decomposition', { from, to, ...options });

export const getProductionMix = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IProductionMixItem[]> =>
	kpiGet<IProductionMixItem[]>('data/sales/production/mix', { from, to, ...options });

// 2.2 Órdenes nuevas

export const getNewOrders = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<INewOrdersItem[]> => kpiGet<INewOrdersItem[]>('data/sales/new-orders', { from, to, ...options });

// 2.3 Saldos pendientes

export const getPendingBalance = (options?: {
	sellerId?: number;
	team?: string;
	severity?: 'ok' | 'vencida' | 'grave';
}): KpiResult<IPendingBalanceItem[]> => kpiGet<IPendingBalanceItem[]>('data/sales/pending-balance', options);

export const getPendingBalanceSummary = (options?: { team?: string }): KpiResult<IPendingBalanceSummaryItem[]> =>
	kpiGet<IPendingBalanceSummaryItem[]>('data/sales/pending-balance/summary', options);

// 2.4 Viajes y emisiones

export const getUpcomingTravels = (options?: {
	weeks?: number;
	sellerId?: number;
	team?: string;
}): KpiResult<IUpcomingTravelItem[]> => kpiGet<IUpcomingTravelItem[]>('data/sales/upcoming-travels', options);

export const getPendingIssues = (options?: { sellerId?: number; team?: string }): KpiResult<IPendingIssueItem[]> =>
	kpiGet<IPendingIssueItem[]>('data/sales/pending-issues', options);

// 2.5 Cobranzas y objetivos

export const getCollections = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<ICollectionsItem[]> => kpiGet<ICollectionsItem[]>('data/sales/collections', { from, to, ...options });

export const getGoalAttainment = async (options?: {
	team?: string;
	refresh?: boolean;
}): Promise<{ data: IGoalAttainmentItem[]; teamTotals: IGoalAttainmentTeamTotal[]; cachedAt: string | null }> => {
	const cleanParams = options
		? Object.fromEntries(Object.entries(options).filter(([, v]) => v !== undefined))
		: undefined;
	const response = await ApiSalesDashboardService.get<
		KpiEnvelope<IGoalAttainmentItem[]> & { teamTotals?: IGoalAttainmentTeamTotal[] }
	>('data/sales/goal-attainment', { params: cleanParams });
	return {
		data: response.data.data,
		teamTotals: response.data.teamTotals ?? [],
		cachedAt: response.data.metadata?.cachedAt ?? null,
	};
};

export const getLeaderboard = (options?: { team?: string }): KpiResult<ILeaderboardItem[]> =>
	kpiGet<ILeaderboardItem[]>('data/sales/leaderboard', options);

// 2.6 Alertas

export const getNegativeVariations = (
	from: string,
	to: string,
	options?: { threshold?: number },
): KpiResult<INegativeVariationItem[]> =>
	kpiGet<INegativeVariationItem[]>('data/sales/alerts/negative-variations', { from, to, ...options });

export const getVariations = (
	from: string,
	to: string,
	options?: {
		sellerId?: number;
		team?: string;
		limit?: number;
		offset?: number;
		onlyNegative?: boolean;
		threshold?: number;
	},
): KpiResult<IVariationItem[]> => kpiGet<IVariationItem[]>('data/sales/variations', { from, to, ...options });

export const getCriticalAlerts = (options?: { sellerId?: number; team?: string }): KpiResult<ICriticalAlertItem[]> =>
	kpiGet<ICriticalAlertItem[]>('data/sales/alerts/critical', options);

// ─── 3. Conversión ───────────────────────────────────────────────────────────

export const getConversion = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IConversionItem[]> =>
	kpiGet<IConversionItem[]>('data/sales-conversion/conversion', { from, to, ...options });

export const getConversionByTeam = (
	from: string,
	to: string,
	options?: { team?: string },
): KpiResult<IConversionByTeamItem[]> =>
	kpiGet<IConversionByTeamItem[]>('data/sales-conversion/conversion/by-team', { from, to, ...options });

export const getPendingConversion = (options?: {
	sellerId?: number;
	team?: string;
	bucket?: string;
	limit?: number;
	offset?: number;
}): KpiResult<IPendingConversionData> =>
	kpiGet<IPendingConversionData>('data/sales-conversion/pending-conversion', options);

export const getPipeline = (options?: { sellerId?: number; team?: string }): KpiResult<IPipelineData> =>
	kpiGet<IPipelineData>('data/sales-conversion/pipeline', options);

// 3.2 Solicitudes operativas

export const getOperationalRequests = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IOperationalRequestItem[]> =>
	kpiGet<IOperationalRequestItem[]>('data/sales-conversion/operational-requests', { from, to, ...options });

export const getOperationalRequestsSummary = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IOperationalRequestSummaryItem[]> =>
	kpiGet<IOperationalRequestSummaryItem[]>('data/sales-conversion/operational-requests/summary', {
		from,
		to,
		...options,
	});

export const getOperationalRequestsByWorkflow = (
	from: string,
	to: string,
): KpiResult<IOperationalRequestByWorkflowItem[]> =>
	kpiGet<IOperationalRequestByWorkflowItem[]>('data/sales-conversion/operational-requests/by-workflow', {
		from,
		to,
	});

export const getOperationalRequestsOverview = (
	from: string,
	to: string,
	options?: { sellerId?: number; team?: string },
): KpiResult<IOperationalRequestOverview> =>
	kpiGet<IOperationalRequestOverview>('data/sales-conversion/operational-requests/overview', {
		from,
		to,
		...options,
	});

// ─── 4. Admin ────────────────────────────────────────────────────────────────

// 4.1 Clasificación de steps

export const getStepClassification = (options?: {
	status?: StepClassification;
	agencyId?: number;
	includeExcluded?: boolean;
}): Promise<IStepClassificationItem[]> =>
	adminGet<IStepClassificationItem[]>('data/sales-conversion/admin/step-classification', options);

export const patchStepClassification = (
	stepId: string,
	classification: StepClassificationWritable,
): Promise<IStepClassificationItem> =>
	adminPatch<IStepClassificationItem>(`data/sales-conversion/admin/step-classification/${stepId}`, { classification });

// 4.2 Exclusión de workflows

export const getWorkflowExclusions = (): Promise<IWorkflowExclusionItem[]> =>
	adminGet<IWorkflowExclusionItem[]>('data/sales-conversion/admin/workflow-exclusions');

export const postWorkflowExclusion = (workflowId: string, reason: string): Promise<IWorkflowExclusionItem> =>
	adminPost<IWorkflowExclusionItem>('data/sales-conversion/admin/workflow-exclusions', {
		workflowId,
		reason,
	});

export const deleteWorkflowExclusion = (workflowId: string): Promise<{ success: boolean; workflowId: string }> =>
	adminDelete<{ success: boolean; workflowId: string }>(
		`data/sales-conversion/admin/workflow-exclusions/${workflowId}`,
	);

// 4.4 Sincronización

export const getSyncStatus = (): Promise<ISyncStatusItem[]> =>
	adminGet<ISyncStatusItem[]>('data/sales-conversion/admin/sync/status');

export const postSyncMetadata = (): Promise<ISyncAcceptedResponse> =>
	adminPost<ISyncAcceptedResponse>('data/sales-conversion/admin/sync/metadata');

export const postSyncTrigger = (): Promise<ISyncAcceptedResponse> =>
	adminPost<ISyncAcceptedResponse>('data/sales-conversion/admin/sync/trigger');

export const postSyncBackfill = (): Promise<ISyncAcceptedResponse> =>
	adminPost<ISyncAcceptedResponse>('data/sales-conversion/admin/sync/backfill');

// 4.5 Workflows del dashboard operativo

export const getDashboardWorkflows = async (): Promise<IDashboardWorkflowItem[]> => {
	const raw = await adminGet<
		IDashboardWorkflowItem[] | { available?: IDashboardWorkflowItem[]; data?: IDashboardWorkflowItem[] }
	>('data/sales-conversion/admin/dashboard-workflows');
	if (Array.isArray(raw)) return raw;
	if (raw && Array.isArray((raw as { available?: IDashboardWorkflowItem[] }).available)) {
		return (raw as { available: IDashboardWorkflowItem[] }).available;
	}
	if (raw && Array.isArray((raw as { data?: IDashboardWorkflowItem[] }).data)) {
		return (raw as { data: IDashboardWorkflowItem[] }).data;
	}
	return [];
};

export const putDashboardWorkflows = (workflowIds: string[]): Promise<{ workflowIds: string[] }> =>
	adminPut<{ workflowIds: string[] }>('data/sales-conversion/admin/dashboard-workflows', { workflowIds });

// ─── 5. Widget operativo del dashboard ───────────────────────────────────────

export const getOperationalDashboard = (options: {
	sellerId?: number;
	team?: string;
}): KpiResult<IOperationalDashboardData> =>
	kpiGet<IOperationalDashboardData>('data/sales-conversion/operational-requests/dashboard', options);
